import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ServicesFAQProps {
  language: 'es' | 'en';
}

export const ServicesFAQ = ({ language }: ServicesFAQProps) => {
  const content = {
    es: {
      title: "Preguntas sobre servicios",
      faqs: [
        {
          question: "¿Qué servicio necesito primero?",
          answer: "Depende de tu situación. Si tu sitio tiene problemas técnicos graves (no indexa, carga lento, errores), lo habitual es empezar por SEO Técnico. Si el objetivo es activar demanda en un mercado local, SEO Local o Google Ads pueden ser el punto de partida. La reunión inicial gratuita de 30 minutos sirve para revisar el contexto y determinar la prioridad."
        },
        {
          question: "¿SEO o Google Ads?",
          answer: "No son excluyentes, son complementarios. El SEO trabaja sobre visibilidad orgánica sostenida en el tiempo. Google Ads puede activar demanda más rápidamente que el SEO, pero el rendimiento depende del mercado, la inversión, la oferta y la medición. La combinación adecuada depende de tu presupuesto, tu urgencia y tus objetivos."
        },
        {
          question: "¿Cuánto dura un proyecto típico?",
          answer: "El SEO es un proceso continuo, no un proyecto con fecha de fin. Los tiempos dependen del punto de partida, la competencia, los recursos disponibles y el alcance acordado. El alcance y la duración se definen por escrito antes de empezar, caso por caso."
        },
        {
          question: "¿Trabajas con empresas fuera de Houston?",
          answer: "Sí. La sede está en Shenandoah, TX (área metropolitana de Houston) y el área de servicio incluye Texas y Florida, además de proyectos en el resto de Estados Unidos. El trabajo es principalmente remoto por videollamada; las reuniones presenciales se coordinan con cita previa."
        },
        {
          question: "¿Puedo contratar solo un servicio?",
          answer: "Sí, cada servicio se puede contratar de forma independiente. Aun así, las disciplinas están conectadas: el SEO técnico condiciona el rendimiento del contenido y de las campañas pagadas. La reunión inicial ayuda a priorizar."
        },
        {
          question: "¿Qué pasa después de la reunión inicial gratuita?",
          answer: "La reunión de 30 minutos sirve para entender el negocio, revisar el contexto y determinar qué tipo de trabajo podría corresponder. Después de la reunión, si existe encaje, puedo proponer un alcance de trabajo. No incluye auditoría, reporte ni plan de acción completo."
        }
      ]
    },
    en: {
      title: "Questions about services",
      faqs: [
        {
          question: "Which service do I need first?",
          answer: "It depends on your situation. If your site has serious technical problems (not indexing, slow loading, errors), Technical SEO is usually the starting point. If the goal is activating demand in a local market, Local SEO or Google Ads may come first. The free 30-minute initial consultation is there to review context and determine the priority."
        },
        {
          question: "SEO or Google Ads?",
          answer: "They're not exclusive, they're complementary. SEO works on sustained organic visibility over time. Google Ads can activate demand faster than SEO, but performance depends on the market, the investment, the offer and the measurement in place. The right mix depends on your budget, urgency and objectives."
        },
        {
          question: "How long does a typical project last?",
          answer: "SEO is an ongoing process, not a project with an end date. Timelines depend on the starting point, competition, available resources and the agreed scope. Scope and duration are defined in writing before work starts, case by case."
        },
        {
          question: "Do you work with companies outside Houston?",
          answer: "Yes. The business address is in Shenandoah, TX (Houston metro area) and the service area covers Texas and Florida, plus projects elsewhere in the United States. Work is primarily remote via video call; in-person meetings are arranged by appointment."
        },
        {
          question: "Can I hire just one service?",
          answer: "Yes, each service can be contracted independently. That said, the disciplines are connected: technical SEO conditions how content and paid campaigns perform. The initial consultation helps you prioritize."
        },
        {
          question: "What happens after the free initial consultation?",
          answer: "The 30-minute meeting is there to understand the business, review context and determine what type of work may apply. After the meeting, if there's a fit, I can propose a scope of work. It does not include an audit, a report or a complete action plan."
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section className="section-padding bg-secondary/30">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          {t.title}
        </h2>
        
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {t.faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-background rounded-xl border border-border px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold hover:no-underline py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
      
      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": t.faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })
      }} />
    </section>
  );
};
